import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { BACKEND_URL } from "@/lib/api";
import { MobileActionBar } from "@/components/MobileActionBar";
import { CleaningNavDropdown, MobileNavMenu } from "@/components/CleaningNavDropdown";
import { useSeo } from "@/lib/useSeo";
import {
  List,
  X,
  Phone,
  ArrowRight,
  CheckCircle,
  Briefcase,
  Globe,
  AppWindow,
  Calculator,
  Megaphone,
  CalendarCheck,
  Funnel,
  UsersThree,
  ChartBar,
  ShieldCheck,
  Lightning,
} from "@phosphor-icons/react";

const PHONE_DISPLAY = "(410) 870-9347";
const PHONE_HREF = "tel:+14108709347";

const BONE = "#F5F4F0";
const CHAR = "#1C1A17";
const FOREST = "#1B2A22";
const TERRA = "#C84B31";
const TERRA_DARK = "#A03A24";

const PILLARS = [
  { title: "Digital presence", body: "Websites, web apps, design, and social media." },
  { title: "Lead pipeline", body: "Prospecting, qualification, and appointments." },
  { title: "Back-office support", body: "Executive assistance, records, and bookkeeping." },
  { title: "One accountable team", body: "One plan, one point of contact, and visible progress." },
];

const SOLUTIONS = [
  { n: "01", icon: Briefcase, title: "Executive Virtual Assistance", body: "Reliable support for inbox and calendar management, client communication, scheduling, research, documents, CRM updates, reports, and everyday administrative work." },
  { n: "02", icon: Globe, title: "Website Development & Design", body: "Conversion-focused websites and landing pages that clearly explain your offer, build trust, capture inquiries, and guide visitors toward the next step." },
  { n: "03", icon: AppWindow, title: "Website & Web App Building", body: "Custom online tools, portals, dashboards, booking experiences, and workflow solutions designed around how your business actually operates." },
  { n: "04", icon: Calculator, title: "Bookkeeping Support", body: "Organized transaction records, invoice tracking, expense categorization, account reconciliation support, and clear financial reporting for better decisions." },
  { n: "05", icon: Megaphone, title: "Social Media Management", body: "Strategy, branded content, scheduling, audience engagement, and performance tracking that keep your business visible and consistent online." },
  { n: "06", icon: CalendarCheck, title: "Appointment Setting", body: "Professional outreach, lead follow-up, qualification, calendar booking, reminders, and pipeline updates so qualified conversations do not get lost." },
  { n: "07", icon: Funnel, title: "Lead Generation", body: "Targeted prospect research, list building, personalized outreach, lead qualification, and follow-up systems that create a steady pipeline of potential clients." },
];

const WHY = [
  { title: "One point of contact", body: "You do not have to chase multiple providers for updates." },
  { title: "The right professionals", body: "Each specialist owns a clear result and supports one coordinated plan." },
  { title: "A controlled workflow", body: "Every task has an owner, deadline, next action, and visible status." },
  { title: "Practical reporting", body: "You see what was completed, what is improving, and what happens next." },
];

const STEPS = [
  { n: "01", title: "Discover", body: "We learn your goals, challenges, audience, workflow, and priorities." },
  { n: "02", title: "Plan", body: "We recommend the right services, scope, timeline, and success measures." },
  { n: "03", title: "Build", body: "Our professionals create your assets, systems, campaigns, and workflows." },
  { n: "04", title: "Manage", body: "We coordinate delivery, provide updates, review results, and improve the plan." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

function StrategyForm() {
  const [f, setF] = useState({ name: "", business: "", email: "", phone: "", message: "", website: "" });
  const [interests, setInterests] = useState([]);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const toggle = (t) =>
    setInterests((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.name.trim() || !f.phone.trim()) {
      setError("Name and phone are required so we can reach you.");
      return;
    }
    setState("sending");
    try {
      await axios.post(`${BACKEND_URL}/api/public/quote-requests`, {
        name: f.name.trim(),
        phone: f.phone.trim(),
        email: f.email.trim() || null,
        service: "Digital Services" + (interests.length ? ` — ${interests.join(" · ")}` : ""),
        timeline: "Free strategy call",
        message:
          (f.business.trim() ? `Business: ${f.business.trim()}\n` : "") +
          (f.message.trim() || "Requested a free strategy call."),
        website: f.website,
      });
      setState("done");
    } catch (err) {
      setState("idle");
      setError(
        err?.response?.data?.detail ||
          `Something went wrong — call ${PHONE_DISPLAY} and we'll take it from there.`
      );
    }
  };

  if (state === "done") {
    return (
      <div className="rounded-sm border border-[#1C1A17]/15 bg-[#FBFAF6] p-8 text-center" data-testid="digital-form-success">
        <CheckCircle size={36} weight="fill" style={{ color: FOREST }} className="mx-auto" />
        <div className="font-display mt-4 text-2xl font-black tracking-tight">Request received.</div>
        <p className="mt-3 text-[14px] text-[#1C1A17]/75">
          We&apos;ll reach out shortly to set up your free strategy call — no pressure and no
          confusing technical language.
        </p>
      </div>
    );
  }

  const input =
    "w-full rounded-sm border border-[#1C1A17]/20 bg-white px-4 py-3 text-[14px] outline-none focus:border-[#C84B31]";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="digital-strategy-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <input className={input} placeholder="Your name *" value={f.name} maxLength={120}
          onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="digital-form-name" />
        <input className={input} placeholder="Business name" value={f.business} maxLength={160}
          onChange={(e) => setF({ ...f, business: e.target.value })} data-testid="digital-form-business" />
        <input className={input} placeholder="Phone *" type="tel" value={f.phone} maxLength={40}
          onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="digital-form-phone" />
        <input className={input} placeholder="Email" type="email" value={f.email} maxLength={160}
          onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="digital-form-email" />
      </div>
      <div>
        <div className="font-mono-label mb-2 text-[10px] tracking-[0.24em] text-[#1C1A17]/60">
          What do you need? (pick any)
        </div>
        <div className="flex flex-wrap gap-2">
          {SOLUTIONS.map((s) => (
            <button
              key={s.title}
              type="button"
              data-testid={`digital-interest-${s.n}`}
              onClick={() => toggle(s.title)}
              className={`rounded-sm border px-3 py-2 text-[12px] font-bold transition-colors ${
                interests.includes(s.title)
                  ? "border-[#C84B31] bg-[#C84B31] text-white"
                  : "border-[#1C1A17]/25 bg-white text-[#1C1A17] hover:border-[#C84B31]"
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>
      </div>
      <textarea className={`${input} min-h-[90px]`} maxLength={1500}
        placeholder="Tell us what is slowing your business down…"
        value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
        data-testid="digital-form-message" />
      <input type="text" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })}
        style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
      {error && <div className="text-[13px] font-bold text-[#A03A24]" data-testid="digital-form-error">{error}</div>}
      <button
        type="submit"
        disabled={state === "sending"}
        data-testid="digital-form-submit"
        className="inline-flex h-12 items-center gap-2 rounded-sm px-7 text-[13px] font-black tracking-wide text-white transition-colors disabled:opacity-60"
        style={{ backgroundColor: TERRA }}
      >
        {state === "sending" ? "SENDING…" : "BOOK A FREE STRATEGY CALL"} <ArrowRight size={15} />
      </button>
      <div className="text-[12px] text-[#1C1A17]/60">
        We will recommend the right next step — with no pressure and no confusing technical language.
      </div>
    </form>
  );
}

export default function DigitalServices() {
  const [menuOpen, setMenuOpen] = useState(false);
  useSeo({
    title: "Digital Services for Local Businesses | Websites, SEO & Ads — HCOB Property Services",
    description: "Websites, Google Business Profile setup, local SEO and ad management for Baltimore and Maryland service businesses. Built and managed by HCOB Property Services.",
    path: "/digital",
  });
  return (
    <div className="min-h-screen" style={{ backgroundColor: BONE, color: CHAR }} data-testid="digital-page">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[#1C1A17]/12 bg-[#F5F4F0]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="digital-brand">
            <div className="grid h-9 w-9 place-items-center rounded-sm text-white" style={{ backgroundColor: FOREST }}>
              <Lightning size={18} weight="fill" />
            </div>
            <div>
              <div className="font-display whitespace-nowrap text-[14px] font-black leading-none tracking-tight sm:text-[15px]">HCOB Property Services</div>
              <div className="font-mono-label mt-1 text-[9px] tracking-[0.28em] text-[#1C1A17]/55">DIGITAL SERVICES</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <nav className="hidden items-center md:flex" aria-label="Primary">
              <CleaningNavDropdown align="right" linkClass="text-[13px] font-semibold text-[#1C1A17]/70 hover:text-[#1C1A17]" />
            </nav>
            <button
              type="button"
              data-testid="mobile-nav-toggle"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen((v) => !v)}
              className="grid h-10 w-10 place-items-center rounded-sm border border-[#1C1A17]/20 text-[#1C1A17] md:hidden"
            >
              {menuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
            </button>
            <a href={PHONE_HREF} data-testid="digital-header-call" aria-label={`Call ${PHONE_DISPLAY}`}
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-sm px-3 text-[12px] font-black text-white sm:px-4" style={{ backgroundColor: TERRA }}>
              <Phone size={14} weight="fill" /> <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
            </a>
          </div>
        </div>
        <MobileNavMenu open={menuOpen} onClose={() => setMenuOpen(false)} anchors={[]} />
      </header>

      {/* Hero */}
      <section className="border-b border-[#1C1A17]/12" data-testid="digital-hero">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
          <div className="font-mono-label text-[10px] tracking-[0.3em]" style={{ color: TERRA }}>
            HCOB DIGITAL SERVICES
          </div>
          <h1 className="font-display mt-5 max-w-3xl text-4xl font-black leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            One team. Every business function. <span style={{ color: TERRA }}>Fully managed.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[#1C1A17]/75">
            Grow your business with a coordinated digital team that handles your online presence, lead
            pipeline, appointments, finances, and daily administration — all under one clear plan.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#strategy" data-testid="digital-hero-cta"
              className="inline-flex h-12 items-center gap-2 rounded-sm px-7 text-[13px] font-black text-white" style={{ backgroundColor: TERRA }}>
              BOOK A FREE STRATEGY CALL <ArrowRight size={15} />
            </a>
            <a href="#solutions" data-testid="digital-hero-secondary"
              className="inline-flex h-12 items-center gap-2 rounded-sm border border-[#1C1A17]/30 px-6 text-[13px] font-bold hover:bg-[#1C1A17] hover:text-[#F5F4F0]">
              Explore Our Solutions
            </a>
          </div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
            className="mt-16 grid grid-cols-1 gap-px border border-[#1C1A17]/15 bg-[#1C1A17]/15 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <motion.div key={p.title} variants={fadeUp} className="bg-[#FBFAF6] p-6" data-testid={`digital-pillar-${i}`}>
                <div className="h-[5px] w-8" style={{ backgroundColor: TERRA }} />
                <div className="font-display mt-4 text-[16px] font-black tracking-tight">{p.title}</div>
                <div className="mt-2 text-[13px] leading-relaxed text-[#1C1A17]/70">{p.body}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Built from real operations */}
      <section className="border-b border-[#F5F4F0]/10" style={{ backgroundColor: FOREST, color: BONE }} data-testid="digital-operations">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-24">
          <div className="font-mono-label text-[10px] tracking-[0.3em]" style={{ color: TERRA }}>
            BUILT FROM REAL OPERATIONS
          </div>
          <h2 className="font-display mt-5 max-w-3xl text-3xl font-black leading-[1.08] tracking-tighter sm:text-4xl">
            HCOB learned how to coordinate people, schedules, clients, and complex service projects.
            Now we bring that same management strength to digital growth.
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-[#F5F4F0]/80">
            Instead of hiring several disconnected freelancers and managing every task yourself, you get
            professionals working together around one business goal. We build the system, assign the
            right people, manage the work, and keep you informed.
          </p>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="border-b border-[#1C1A17]/12" data-testid="digital-solutions">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
          <div className="font-mono-label text-[10px] tracking-[0.28em] text-[#1C1A17]/55">
            OUR DIGITAL SOLUTIONS
          </div>
          <h2 className="font-display mt-4 max-w-2xl text-4xl font-black leading-[1.02] tracking-tighter sm:text-5xl">
            The support your business needs to look professional, attract opportunities, and
            <span style={{ color: TERRA }}> operate smoothly.</span>
          </h2>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger}
            className="mt-14 grid grid-cols-1 gap-px border border-[#1C1A17]/15 bg-[#1C1A17]/15 sm:grid-cols-2 lg:grid-cols-3">
            {SOLUTIONS.map((s) => {
              const I = s.icon;
              return (
                <motion.div key={s.n} variants={fadeUp} className="group bg-[#FBFAF6] p-6 transition-colors hover:bg-[#F5F4F0]" data-testid={`digital-solution-${s.n}`}>
                  <div className="flex items-center justify-between">
                    <div className="grid h-10 w-10 place-items-center rounded-sm border border-[#1C1A17]/15 bg-[#F5F4F0] transition-transform group-hover:scale-110 group-hover:border-[#C84B31]" style={{ color: FOREST }}>
                      <I size={20} weight="duotone" />
                    </div>
                    <div className="font-mono-label text-[11px] tracking-[0.24em] text-[#1C1A17]/40">{s.n}</div>
                  </div>
                  <div className="font-display mt-4 text-[16px] font-black tracking-tight">{s.title}</div>
                  <div className="mt-2 text-[13px] leading-relaxed text-[#1C1A17]/70">{s.body}</div>
                </motion.div>
              );
            })}
            <motion.div variants={fadeUp} className="flex flex-col justify-between p-6 text-white" style={{ backgroundColor: TERRA }} data-testid="digital-solution-custom">
              <div>
                <div className="font-display text-[16px] font-black tracking-tight">Need a combination?</div>
                <div className="mt-2 text-[13px] leading-relaxed text-white/85">
                  We can build a managed package around your priorities, team size, and growth stage.
                </div>
              </div>
              <a href="#strategy" className="mt-6 inline-flex min-h-[44px] items-center gap-2 text-[13px] font-black" data-testid="digital-build-team-cta">
                BUILD MY DIGITAL TEAM <ArrowRight size={14} />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why HCOB */}
      <section className="border-b border-[#1C1A17]/12" data-testid="digital-why">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-24">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="font-mono-label text-[10px] tracking-[0.28em] text-[#1C1A17]/55">WHY HCOB</div>
              <h2 className="font-display mt-4 text-3xl font-black leading-[1.05] tracking-tighter sm:text-4xl">
                Digital services work better when every professional is moving in
                <span style={{ color: TERRA }}> the same direction.</span>
              </h2>
              <p className="mt-6 text-[14px] leading-relaxed text-[#1C1A17]/70">
                Hiring separate providers often leaves the business owner coordinating the website
                developer, social media manager, appointment setter, bookkeeper, and virtual assistant.
                When communication breaks down, leads are missed, projects slow down, and no one owns
                the final result.
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="font-mono-label mb-4 text-[10px] tracking-[0.24em] text-[#1C1A17]/55">With HCOB, you get:</div>
              <div className="grid gap-px border border-[#1C1A17]/15 bg-[#1C1A17]/15 sm:grid-cols-2">
                {WHY.map((w, i) => (
                  <div key={w.title} className="bg-[#FBFAF6] p-6" data-testid={`digital-why-${i}`}>
                    <ShieldCheck size={18} weight="fill" style={{ color: FOREST }} />
                    <div className="font-display mt-3 text-[15px] font-black tracking-tight">{w.title}</div>
                    <div className="mt-1.5 text-[13px] leading-relaxed text-[#1C1A17]/70">{w.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-[#1C1A17]/12" data-testid="digital-process">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-24">
          <div className="font-mono-label text-[10px] tracking-[0.28em] text-[#1C1A17]/55">HOW IT WORKS</div>
          <h2 className="font-display mt-4 max-w-2xl text-3xl font-black leading-[1.05] tracking-tighter sm:text-4xl">
            A simple process from business need to <span style={{ color: TERRA }}>measurable execution.</span>
          </h2>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger}
            className="mt-12 grid grid-cols-1 gap-px border border-[#1C1A17]/15 bg-[#1C1A17]/15 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <motion.div key={s.n} variants={fadeUp} className="bg-[#FBFAF6] p-6" data-testid={`digital-step-${s.n}`}>
                <div className="font-mono-label text-[12px] tracking-[0.24em]" style={{ color: TERRA }}>{s.n}</div>
                <div className="font-display mt-3 text-[17px] font-black tracking-tight">{s.title}</div>
                <div className="mt-2 text-[13px] leading-relaxed text-[#1C1A17]/70">{s.body}</div>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-12 flex flex-wrap items-start gap-4 rounded-sm border border-dashed border-[#1C1A17]/30 bg-[#FBFAF6] p-6" data-testid="digital-professionals">
            <UsersThree size={26} weight="duotone" style={{ color: FOREST }} />
            <div className="min-w-0 flex-1">
              <div className="font-display text-[15px] font-black tracking-tight">Our professionals — a flexible team built around your business.</div>
              <p className="mt-2 max-w-3xl text-[13px] leading-relaxed text-[#1C1A17]/70">
                Your HCOB digital team may include an account manager, executive virtual assistant,
                website developer, web app builder, bookkeeper, social media specialist, lead generation
                professional, or appointment setter. You receive the expertise you need without having
                to recruit, train, and coordinate every role yourself.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA + form */}
      <section id="strategy" style={{ backgroundColor: FOREST, color: BONE }} data-testid="digital-cta">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-5">
            <div className="font-mono-label text-[10px] tracking-[0.3em]" style={{ color: TERRA }}>
              READY TO GROW WITH LESS GUESSWORK?
            </div>
            <h2 className="font-display mt-4 text-3xl font-black leading-[1.05] tracking-tighter sm:text-4xl lg:text-5xl">
              Let us build the digital support system behind your next stage of growth.
            </h2>
            <p className="mt-6 text-[14px] leading-relaxed text-[#F5F4F0]/80">
              Whether you need one focused service or a fully managed team, HCOB will help you clarify
              the priorities, organize the work, and move forward with confidence.
            </p>
            <div className="mt-8 rounded-sm border border-[#F5F4F0]/20 p-5">
              <div className="text-[13px] leading-relaxed text-[#F5F4F0]/80">
                Built for contractors, property-service companies, local businesses, and growing teams
                that need reliable digital execution without the burden of managing multiple providers.
              </div>
            </div>
            <div className="mt-6 text-[13px] text-[#F5F4F0]/70">
              Prefer to talk? Call{" "}
              <a href={PHONE_HREF} className="font-bold text-white underline underline-offset-2">{PHONE_DISPLAY}</a>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-sm bg-[#FBFAF6] p-5 text-[#1C1A17] md:p-7">
              <StrategyForm />
            </div>
          </div>
        </div>
      </section>

      {/* Contact bar footer */}
      <footer className="border-t border-[#F5F4F0]/10 py-8" style={{ backgroundColor: CHAR, color: "#F5F4F0" }} data-testid="digital-footer">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-2 px-5 md:flex-row md:items-center md:justify-between md:px-10">
          <div className="text-[13px] font-bold">One team. Every business function. Fully managed.</div>
          <div className="text-[12px] text-[#F5F4F0]/70">
            HCOB Property Services — Baltimore, Maryland · <a href={PHONE_HREF} className="font-bold text-white">{PHONE_DISPLAY}</a> ·{" "}
            <Link to="/" className="underline underline-offset-2">hcobnetwork.com</Link>
          </div>
        </div>
      </footer>
      <MobileActionBar
        primaryLabel="Book a Free Strategy Call"
        primaryHref="#strategy"
        phoneHref={PHONE_HREF}
        textBody="Hi HCOB Digital — I'd like to talk about "
        watch={['[data-testid="digital-hero"]', "#strategy"]}
        testid="digital-mobile-bar"
      />
    </div>
  );
}
